import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import '../styles/LoanForm.css';

function LoaneeRequestForm({ onSuccess }) {
  const { currentUser } = useAuth();
  const [loaners, setLoaners] = useState([]);
  const [formData, setFormData] = useState({
    amount: '',
    purpose: '',
    notes: '',
    assignedLoanerId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLoaners();
  }, []);

  async function fetchLoaners() {
    try {
      const loanersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'loaner')
      );
      const snapshot = await getDocs(loanersQuery);
      let loanersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter out test users unless dev mode is enabled
      const devModeEnabled = localStorage.getItem('devModeEnabled') === 'true';
      if (!devModeEnabled) {
        loanersList = loanersList.filter(loaner => 
          !loaner.id?.startsWith('dev-') && 
          loaner.role !== 'test' &&
          !loaner.isTestUser
        );
      }

      setLoaners(loanersList);
    } catch (err) {
      console.error('Error fetching loaners:', err);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid loan amount');
      return;
    }

    if (!formData.purpose.trim()) {
      setError('Please provide a purpose for the loan');
      return;
    }

    if (!formData.assignedLoanerId) {
      setError('Please select a loaner to review your request');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const selectedLoaner = loaners.find(l => l.id === formData.assignedLoanerId);

      await addDoc(collection(db, 'loanRequests'), {
        loaneeId: currentUser.uid,
        loaneeName: currentUser.displayName,
        loaneeEmail: currentUser.email,
        amount: parseFloat(formData.amount),
        purpose: formData.purpose,
        notes: formData.notes,
        status: 'pending',
        createdAt: new Date().toISOString(),
        assignedLoanerId: formData.assignedLoanerId,
        assignedLoanerName: selectedLoaner?.displayName || 'Unknown',
        assignedAt: new Date().toISOString()
      });

      setFormData({ amount: '', purpose: '', notes: '', assignedLoanerId: '' });
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Failed to submit loan request');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="loan-form-container">
      <h2>Request a Loan</h2>
      
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="loan-form">
        <div className="input-group">
          <label htmlFor="assignedLoanerId">Select Loaner</label>
          <select
            id="assignedLoanerId"
            name="assignedLoanerId"
            value={formData.assignedLoanerId}
            onChange={handleChange}
            className="select-field"
            required
          >
            <option value="">-- Select a loaner --</option>
            {loaners.map(loaner => (
              <option key={loaner.id} value={loaner.id}>
                {loaner.displayName} ({loaner.email})
              </option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="amount">Loan Amount ($)</label>
          <input
            id="amount"
            name="amount"
            type="number"
            step="0.01"
            min="0"
            value={formData.amount}
            onChange={handleChange}
            className="input-field"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="purpose">Purpose</label>
          <input
            id="purpose"
            name="purpose"
            type="text"
            value={formData.purpose}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., Home improvement, Education, Medical"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="notes">Additional Notes (Optional)</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="textarea-field"
            rows="4"
            placeholder="Any additional information..."
          />
        </div>

        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </button>
      </form>
    </div>
  );
}

export default LoaneeRequestForm;
